<template>
  <div class="logs-container">
    <!-- En-tête -->
    <div class="header-section mb-6">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">
        <i class="fas fa-clipboard-list mr-3"></i>Consultation des Logs API
      </h1>
      <p class="text-gray-600">Consultez, filtrez et analysez les logs de votre API</p>
    </div>

    <!-- Navigation par onglets -->
    <div class="tabs-container mb-6">
      <div class="flex border-b border-gray-200">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'px-6 py-3 font-medium text-sm transition-all',
            activeTab === tab.id
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-600 hover:text-green-600'
          ]"
        >
          <i :class="tab.icon + ' mr-2'"></i>{{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Contenu des onglets -->
    <div class="tab-content">
      <!-- Onglet Logs -->
      <div v-show="activeTab === 'logs'">
        <LogsFilters 
          v-model:filters="filters" 
          @search="fetchLogs" 
          @reset="resetFilters"
          @export="exportLogs"
        />
        <LogsStats :stats="stats" class="mb-6" />
        <LogsTable 
          :logs="logs" 
          :loading="loading"
          :pagination="pagination"
          @page-change="changePage"
          @view-details="viewLogDetails"
        />
      </div>

      <!-- Onglet Statistiques -->
      <div v-show="activeTab === 'stats'">
        <LogsStatistics 
          :globalStats="globalStats"
          :topEndpoints="topEndpoints"
          :topErrors="topErrors"
          :topUsers="topUsers"
          :loading="statsLoading"
          @refresh="fetchStatistics"
        />
      </div>

      <!-- Onglet Maintenance -->
      <div v-show="activeTab === 'maintenance'">
        <LogsMaintenance @cleanup="handleCleanup" />
      </div>
    </div>

    <!-- Modal détails log -->
    <LogDetailsModal 
      v-if="selectedLog"
      :log="selectedLog"
      @close="selectedLog = null"
    />

    <!-- Notifications -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <i :class="notificationIcon"></i>
      {{ notification.message }}
    </div>
  </div>
</template>

<script>
import api from '../plugins/axios';
import LogsFilters from '../components/logs/LogsFilters.vue';
import LogsStats from '../components/logs/LogsStats.vue';
import LogsTable from '../components/logs/LogsTable.vue';
import LogsStatistics from '../components/logs/LogsStatistics.vue';
import LogsMaintenance from '../components/logs/LogsMaintenance.vue';
import LogDetailsModal from '../components/logs/LogDetailsModal.vue';

export default {
  name: 'LogsView',
  components: {
    LogsFilters,
    LogsStats,
    LogsTable,
    LogsStatistics,
    LogsMaintenance,
    LogDetailsModal,
  },
  data() {
    return {
      activeTab: 'logs',
      tabs: [
        { id: 'logs', label: 'Logs', icon: 'fas fa-list' },
        { id: 'stats', label: 'Statistiques', icon: 'fas fa-chart-bar' },
        { id: 'maintenance', label: 'Maintenance', icon: 'fas fa-tools' },
      ],
      filters: {
        page: 1,
        limit: 100,
        status: '',
        statusCode: '',
        userId: '',
        endpoint: '',
        search: '',
        dateFrom: '',
        dateTo: '',
        sortBy: 'created_at',
        sortOrder: 'DESC',
      },
      logs: [],
      stats: {
        total_requests: 0,
        success_count: 0,
        failure_count: 0,
        avg_response_time: 0,
        min_response_time: 0,
        max_response_time: 0,
      },
      pagination: {
        page: 1,
        limit: 100,
        total: 0,
        totalPages: 0,
      },
      loading: false,
      statsLoading: false,
      selectedLog: null,
      globalStats: null,
      topEndpoints: [],
      topErrors: [],
      topUsers: [],
      notification: {
        show: false,
        message: '',
        type: 'success',
      },
    };
  },
  computed: {
    notificationIcon() {
      return this.notification.type === 'success' 
        ? 'fas fa-check-circle' 
        : 'fas fa-exclamation-circle';
    },
  },
  mounted() {
    this.fetchLogs();
    this.fetchQuickStats();
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const params = this.buildQueryParams();
        const response = await api.get('/admin2/logs', { params });
        
        if (response.data.success) {
          this.logs = response.data.data;
          this.pagination = response.data.pagination;
        }
      } catch (error) {
        this.showNotification('Erreur lors du chargement des logs', 'error');
        console.error('Erreur:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchQuickStats() {
      try {
        const params = {};
        if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
        if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
        if (this.filters.userId) params.userId = this.filters.userId;

        const response = await api.get('/admin2/logs/stats', { params });
        if (response.data.success) {
          this.stats = response.data.data;
        }
      } catch (error) {
        console.error('Erreur stats:', error);
      }
    },

    async fetchStatistics() {
      this.statsLoading = true;
      console.log('🔍 [Frontend] Fetching statistics with limit=10');
      try {
        const [statsRes, endpointsRes, errorsRes, usersRes] = await Promise.all([
          api.get('/admin2/logs/stats'),
          api.get('/admin2/logs/top-endpoints', { params: { limit: 10 } }),
          api.get('/admin2/logs/top-errors', { params: { limit: 10 } }),
          api.get('/admin2/logs/top-users', { params: { limit: 10 } }),
        ]);
        
        console.log('✅ [Frontend] Statistics received:', {
          stats: statsRes.data,
          endpoints: endpointsRes.data,
          errors: errorsRes.data,
          users: usersRes.data
        });

        if (statsRes.data.success) {
          this.globalStats = statsRes.data.data;
        }
        if (endpointsRes.data.success) {
          this.topEndpoints = endpointsRes.data.data;
        }
        if (errorsRes.data.success) {
          this.topErrors = errorsRes.data.data;
        }
        if (usersRes.data.success) {
          this.topUsers = usersRes.data.data;
        }
      } catch (error) {
        this.showNotification('Erreur : L\'API n\'est pas accessible ou les endpoints /admin2/logs/* n\'existent pas encore', 'error');
        console.error('Erreur détaillée:', error.response || error);
      } finally {
        this.statsLoading = false;
      }
    },

    buildQueryParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      return params;
    },

    async exportLogs() {
      try {
        const params = this.buildQueryParams();
        delete params.page;
        delete params.limit;

        const response = await api.get('/admin2/logs/export', { 
          params,
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `logs-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.showNotification('Export CSV réussi', 'success');
      } catch (error) {
        this.showNotification('Erreur lors de l\'export', 'error');
        console.error('Erreur:', error);
      }
    },

    async handleCleanup(days) {
      try {
        const response = await api.delete('/admin2/logs/cleanup', {
          data: { days }
        });

        if (response.data.success) {
          this.showNotification(response.data.message, 'success');
          this.fetchLogs();
          this.fetchQuickStats();
        }
      } catch (error) {
        this.showNotification('Erreur lors du nettoyage', 'error');
        console.error('Erreur:', error);
      }
    },

    changePage(page) {
      this.filters.page = page;
      this.fetchLogs();
    },

    viewLogDetails(log) {
      this.selectedLog = log;
    },

    resetFilters() {
      this.filters = {
        page: 1,
        limit: 100,
        status: '',
        statusCode: '',
        userId: '',
        endpoint: '',
        search: '',
        dateFrom: '',
        dateTo: '',
        sortBy: 'created_at',
        sortOrder: 'DESC',
      };
      this.fetchLogs();
      this.fetchQuickStats();
    },

    showNotification(message, type = 'success') {
      this.notification = { show: true, message, type };
      setTimeout(() => {
        this.notification.show = false;
      }, 3000);
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'stats') {
        this.fetchStatistics();
      }
    },
    'filters.dateFrom'() {
      this.fetchQuickStats();
    },
    'filters.dateTo'() {
      this.fetchQuickStats();
    },
    'filters.userId'() {
      this.fetchQuickStats();
    },
  },
};
</script>

<style scoped>
.logs-container {
  max-width: 100%;
  padding: 1rem;
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  animation: slideIn 0.3s ease-out;
  font-weight: 500;
}

.notification.success {
  background-color: #10b981;
  color: white;
}

.notification.error {
  background-color: #ef4444;
  color: white;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.tabs-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-section {
  padding: 1rem 0;
}
</style>

